// src/components/analytics/RequestsDrillModal.tsx
/**
 * Modal riutilizzabile di drill-down: mostra l'elenco delle richieste che compongono
 * un elemento di un grafico. Ogni riga apre il dettaglio della richiesta in una NUOVA TAB
 * (/requests/{id}). Due modalità:
 *  - rows: righe già disponibili lato client (grafici tempi di completamento);
 *  - query: fetch on-demand all'apertura (grafici aggregati esistenti).
 * L'intestazione di ogni colonna è cliccabile per ordinare (asc → desc → ordine originale).
 */

import React from 'react';
import {
  Modal,
  Table,
  ScrollArea,
  Badge,
  Text,
  Anchor,
  Center,
  Loader,
  Stack,
  Box,
  Group,
} from '@mantine/core';
import {
  IconExternalLink,
  IconInbox,
  IconChevronUp,
  IconChevronDown,
  IconSelector,
} from '@tabler/icons-react';
import { RequestAnalyticsRow, AnalyticsRequestsQuery } from '../../types/api';
import { useAnalyticsRequests } from '../../hooks/use-analytics-requests';
import { TYPE_META, STATUS_META, typeLabel, statusLabel, requestDetailPath } from './analytics-meta';

interface RequestsDrillModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  /** Righe già disponibili (modalità diretta, senza fetch) */
  rows?: RequestAnalyticsRow[];
  /** Filtro per fetch on-demand (modalità aggregati) */
  query?: AnalyticsRequestsQuery;
}

type SortDir = 'asc' | 'desc';
type SortKey = 'number' | 'type' | 'status' | 'client' | 'title' | 'created' | 'completed' | 'days';

interface ColumnDef {
  key: SortKey;
  label: string;
  alignRight?: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: 'number', label: 'N°' },
  { key: 'type', label: 'Tipo' },
  { key: 'status', label: 'Stato' },
  { key: 'client', label: 'Cliente' },
  { key: 'title', label: 'Titolo' },
  { key: 'created', label: 'Creata' },
  { key: 'completed', label: 'Completata' },
  { key: 'days', label: 'Giorni', alignRight: true },
];

function formatDate(value?: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Valore ordinabile per una colonna; null = valore assente (verrà messo in fondo). */
function sortValue(r: RequestAnalyticsRow, key: SortKey): number | string | null {
  switch (key) {
    case 'number':
      return r.progressiveYear * 1_000_000 + r.progressiveNumber;
    case 'type':
      return r.type;
    case 'status':
      return r.status;
    case 'client':
      return r.clientName?.trim() || null;
    case 'title':
      return r.title?.trim() || null;
    case 'created':
      return r.createdAt ? new Date(r.createdAt).getTime() : null;
    case 'completed':
      return r.completedAt ? new Date(r.completedAt).getTime() : null;
    case 'days':
      return r.durationDays ?? null;
    default:
      return null;
  }
}

export function RequestsDrillModal({
  opened,
  onClose,
  title,
  subtitle,
  rows,
  query,
}: RequestsDrillModalProps) {
  const useFetch = !rows;
  const { data: fetched, isLoading, isError } = useAnalyticsRequests(query, opened && useFetch);

  const data: RequestAnalyticsRow[] = rows ?? fetched ?? [];

  const [sort, setSort] = React.useState<{ key: SortKey; dir: SortDir } | null>(null);

  // Reset dell'ordinamento a ogni nuovo drill (cambio di titolo) o riapertura.
  React.useEffect(() => {
    if (opened) setSort(null);
  }, [opened, title]);

  const handleSort = (key: SortKey) =>
    setSort((prev) =>
      !prev || prev.key !== key
        ? { key, dir: 'asc' }
        : prev.dir === 'asc'
          ? { key, dir: 'desc' }
          : null // terzo click: torna all'ordine originale
    );

  const sortedData = React.useMemo(() => {
    if (!sort) return data;
    const { key, dir } = sort;
    const factor = dir === 'asc' ? 1 : -1;
    const present: RequestAnalyticsRow[] = [];
    const missing: RequestAnalyticsRow[] = [];
    for (const r of data) {
      if (sortValue(r, key) === null) missing.push(r);
      else present.push(r);
    }
    present.sort((a, b) => {
      const av = sortValue(a, key)!;
      const bv = sortValue(b, key)!;
      const base =
        typeof av === 'string' || typeof bv === 'string'
          ? String(av).localeCompare(String(bv), 'it', { sensitivity: 'base' })
          : (av as number) - (bv as number);
      return factor * base;
    });
    return [...present, ...missing]; // valori assenti sempre in fondo
  }, [data, sort]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="90%"
      title={
        <Stack gap={2}>
          <Text fw={600}>{title}</Text>
          {subtitle && (
            <Text size="xs" c="dimmed">
              {subtitle}
            </Text>
          )}
        </Stack>
      }
    >
      {useFetch && isLoading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : isError ? (
        <Center py="xl">
          <Text c="red">Errore nel caricamento delle richieste.</Text>
        </Center>
      ) : data.length === 0 ? (
        <Center py="xl">
          <Stack align="center" gap="xs">
            <IconInbox size={32} opacity={0.5} />
            <Text c="dimmed">Nessuna richiesta per questa selezione.</Text>
          </Stack>
        </Center>
      ) : (
        <Stack gap="xs">
          <Text size="sm" c="dimmed">
            {data.length} {data.length === 1 ? 'richiesta' : 'richieste'} · clicca
            un'intestazione per ordinare
          </Text>
          <ScrollArea.Autosize mah="calc(100vh - 220px)">
            <Table striped highlightOnHover stickyHeader withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  {COLUMNS.map((col) => {
                    const active = sort?.key === col.key;
                    const SortIcon = !active
                      ? IconSelector
                      : sort!.dir === 'asc'
                        ? IconChevronUp
                        : IconChevronDown;
                    return (
                      <Table.Th
                        key={col.key}
                        onClick={() => handleSort(col.key)}
                        style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
                      >
                        <Group
                          gap={4}
                          wrap="nowrap"
                          justify={col.alignRight ? 'flex-end' : 'flex-start'}
                        >
                          <span>{col.label}</span>
                          <SortIcon size={14} style={{ opacity: active ? 0.9 : 0.35 }} />
                        </Group>
                      </Table.Th>
                    );
                  })}
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sortedData.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td>
                      <Anchor
                        href={requestDetailPath(r.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        fw={600}
                      >
                        {r.progressiveNumberFormatted}
                      </Anchor>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={TYPE_META[r.type]?.mantineColor ?? 'gray'} variant="light" size="sm">
                        {typeLabel(r.type)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={STATUS_META[r.status]?.mantineColor ?? 'gray'} variant="dot" size="sm">
                        {statusLabel(r.status)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1}>
                        {r.clientName || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={1} maw={320}>
                        {r.title || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.createdAt)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(r.completedAt)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      {r.durationDays != null ? (
                        <Text size="sm" fw={600}>
                          {r.durationDays.toFixed(1)}
                        </Text>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Anchor
                        href={requestDetailPath(r.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Apri dettaglio in una nuova tab"
                      >
                        <Center>
                          <IconExternalLink size={16} />
                        </Center>
                      </Anchor>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </Stack>
      )}
    </Modal>
  );
}

/** Box vuoto: riutilizzabile per stati "nessun dato" nei grafici. */
export function DrillHint() {
  return (
    <Box>
      <Text size="xs" c="dimmed">
        Clicca su un elemento del grafico per vedere le richieste che lo compongono.
      </Text>
    </Box>
  );
}
